import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';

const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export default function Exam() {
  const { questionnaireId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(location.state?.language || 'malayalam');
  const [syncedAnswers, setSyncedAnswers] = useState({});
  const [examId, setExamId] = useState(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [duration, setDuration] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [adminLocked, setAdminLocked] = useState(false);
  const [lockPending, setLockPending] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const timerRef = useRef(null);
  const userAnswersRef = useRef(userAnswers);
  const examIdRef = useRef(examId);
  const lockPendingRef = useRef(lockPending);

  const [offlineTime, setOfflineTime] = useState(0);
  const offlineStartRef = useRef(null);

  useEffect(() => {
    const handleOffline = () => {
      offlineStartRef.current = Date.now();
    };
    const handleOnline = () => {
      if (offlineStartRef.current) {
        const dur = Math.floor((Date.now() - offlineStartRef.current) / 1000);
        setOfflineTime(prev => prev + dur);
        offlineStartRef.current = null;
      }
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Keep refs up to date
  useEffect(() => {
    userAnswersRef.current = userAnswers;
  }, [userAnswers]);

  useEffect(() => {
    examIdRef.current = examId;
  }, [examId]);

  useEffect(() => {
    lockPendingRef.current = lockPending;
  }, [lockPending]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    loadQuestions();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [questionnaireId]);

  useEffect(() => {
    if (started && timeLeft > 0 && !finished && startTime) {
      timerRef.current = setInterval(() => {
        // Calculate exact real-world time left (immune to background tab throttling)
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        const realTimeLeft = 10 * 60 - Math.max(0, elapsedSeconds);

        setTimeLeft(t => {
          if (realTimeLeft <= 0) return 0; // Just return 0, the other useEffect will catch it
          
          // Save timeLeft to localStorage
          try {
            const savedState = JSON.parse(localStorage.getItem(`exam_state_${questionnaireId}`) || '{}');
            savedState.timeLeft = realTimeLeft;
            localStorage.setItem(`exam_state_${questionnaireId}`, JSON.stringify(savedState));
          } catch (e) {
            console.error('Could not save time');
          }
          return realTimeLeft;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [started, finished, startTime, duration]);

  // Auto-submit when time reaches 0 (ensures fresh userAnswers state)
  useEffect(() => {
    if (started && timeLeft === 0 && !finished) {
      finishExam();
    }
  }, [timeLeft, started, finished, userAnswers]);

  // Background sync: save unsynced answers every 30 seconds
  useEffect(() => {
    if (!started || !examId || finished) return;

    const syncTimer = setInterval(async () => {
      const currentAnswers = userAnswersRef.current;
      const currentExamId = examIdRef.current;
      
      const unsynced = Object.entries(currentAnswers).filter(
        ([qId]) => !syncedAnswers[qId]
      );

      console.log('Background sync check - unsynced count:', unsynced.length);

      if (unsynced.length > 0) {
        try {
          const chunk = unsynced.slice(0, 10).map(([qId, aId]) => ({
            question_id: parseInt(qId),
            answer_id: parseInt(aId)
          }));

          console.log('Sending chunk to server:', chunk);
          await api.post('/exam/save-chunk', {
            exam_id: currentExamId,
            answers: chunk
          });

          // Mark as synced
          const newSynced = { ...syncedAnswers };
          chunk.forEach(({ question_id }) => {
            newSynced[question_id] = true;
          });
          setSyncedAnswers(newSynced);
          console.log('Chunk synced successfully');
        } catch (e) {
          console.error('Background sync failed:', e);
        }
      }
    }, 5000); // 5 seconds — backend write-behind handles the DB load

    return () => clearInterval(syncTimer);
  }, [started, examId, syncedAnswers, finished]);

  const loadQuestions = async () => {
    try {
      // Check if exam is already submitted
      try {
        const { data: examCheck } = await api.get(`/exam/check-submission/${questionnaireId}`);
        console.log('--- check-submission result ---', examCheck);
        if (examCheck.submitted) {
          console.log('Exam is marked as submitted/expired. Kicking to dashboard.');
          const isTest = new URLSearchParams(location.search).get('testChannel') === 'true';
          navigate(isTest ? '/student-react/test-channel' : '/student-react/dashboard');
          return;
        }
      } catch (e) {
        // If check fails, continue loading (might be old endpoint)
      }

      const { data } = await api.get(`/exam/questionnaire/${questionnaireId}/questions`);
      
      let questionsToSet = data.questions;
      try {
        const savedOrderStr = localStorage.getItem(`exam_order_${questionnaireId}`);
        if (savedOrderStr) {
          const savedOrder = JSON.parse(savedOrderStr);
          if (savedOrder.length === data.questions.length) {
            questionsToSet = [...data.questions].sort((a, b) => savedOrder.indexOf(a.id) - savedOrder.indexOf(b.id));
          } else {
            questionsToSet = shuffleArray(data.questions);
            localStorage.setItem(`exam_order_${questionnaireId}`, JSON.stringify(questionsToSet.map(q => q.id)));
          }
        } else {
          questionsToSet = shuffleArray(data.questions);
          localStorage.setItem(`exam_order_${questionnaireId}`, JSON.stringify(questionsToSet.map(q => q.id)));
        }
      } catch (e) {
        questionsToSet = shuffleArray(data.questions);
      }
      
      setQuestions(questionsToSet);
      setAnswers(data.answers);
      setQuestionnaire(data.questionnaire || null);
      
      // Attempt to resume from backend first!
      try {
        const { data: resumeData } = await api.get(`/exam/resume/${questionnaireId}`);
        console.log('--- resume response ---', resumeData);
        if (resumeData.ongoing) {
          console.log('Resuming from backend! Remaining time:', resumeData.remaining_time);
          setExamId(resumeData.exam_id);
          setStarted(true);
          const dur = 10;
          setDuration(dur);
          
          const elapsedSeconds = (dur * 60) - resumeData.remaining_time;
          setStartTime(Date.now() - (elapsedSeconds * 1000));
          setUserAnswers(resumeData.answers);
          
          setTimeLeft(Math.max(0, resumeData.remaining_time));
          
          localStorage.setItem(`exam_${questionnaireId}`, JSON.stringify(resumeData.answers));
          
          setLoading(false);
          return; // Skip localStorage loading since we have fresh backend data
        } else {
          // Backend explicitly confirmed NO ONGOING EXAM
          // Clear any ghost data from localStorage
          localStorage.removeItem(`exam_${questionnaireId}`);
          localStorage.removeItem(`exam_state_${questionnaireId}`);
        }
      } catch (resumeErr) {
        console.error('Could not fetch resume data:', resumeErr);
      }

      // Load saved answers from localStorage (Fallback for offline/not ongoing)
      try {
        const saved = localStorage.getItem(`exam_${questionnaireId}`);
        if (saved) {
          setUserAnswers(JSON.parse(saved));
        }
      } catch (e) {
        console.error('Could not load saved answers');
      }
      
      // Load exam state from localStorage
      try {
        const savedState = localStorage.getItem(`exam_state_${questionnaireId}`);
        if (savedState) {
          const state = JSON.parse(savedState);
          if (state.started) {
            setExamId(state.examId);
            setStarted(true);
            setDuration(state.duration);
            setCurrentPage(state.currentPage || 0);
            if (state.startTime) {
              setStartTime(state.startTime);
              const elapsedSeconds = Math.floor((Date.now() - state.startTime) / 1000);
              const realTimeLeft = (state.duration || 10) * 60 - Math.max(0, elapsedSeconds);
              setTimeLeft(Math.max(0, realTimeLeft));
            } else {
              setTimeLeft(state.timeLeft);
            }
            if (state.language) {
              setSelectedLanguage(state.language);
            } else {
              setSelectedLanguage('malayalam'); // Fallback
            }
          }
        }
      } catch (e) {
        console.error('Could not load exam state', e);
      }
    } catch (err) {
      setError('Failed to load exam');
    } finally {
      setLoading(false);
    }
  };

  const startExam = async () => {
    try {
      const { data } = await api.post('/exam/start', { 
        questionnaire_id: questionnaireId,
        language: selectedLanguage
      });
      setExamId(data.exam_id);
      setDuration(10);
      setTimeLeft(data.remaining_time !== undefined ? data.remaining_time : 10 * 60);
      setStarted(true);
      setStartTime(Date.now());
      window.scrollTo(0, 0);
      
      // Save exam state to localStorage
      try {
        localStorage.setItem(`exam_state_${questionnaireId}`, JSON.stringify({
          examId: data.exam_id,
          started: true,
          duration: 10,
          timeLeft: data.remaining_time !== undefined ? data.remaining_time : 10 * 60,
          currentPage: 0,
          startTime: Date.now(),
          language: selectedLanguage
        }));
      } catch (e) {
        console.error('Could not save exam state');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start exam');
    }
  };

  const handleAnswerSelect = (questionId, answerId) => {
    setUserAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: answerId };
      // Save to localStorage with fallback to sessionStorage
      try {
        localStorage.setItem(`exam_${questionnaireId}`, JSON.stringify(newAnswers));
      } catch (e) {
        try {
          sessionStorage.setItem(`exam_${questionnaireId}`, JSON.stringify(newAnswers));
        } catch (e2) {
          console.error('Storage not available');
        }
      }
      return newAnswers;
    });
  };

  const nextPage = () => {
    const totalPages = Math.ceil(questions.length / 5);
    if (currentPage < totalPages - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      // Save page to localStorage
      try {
        const savedState = JSON.parse(localStorage.getItem(`exam_state_${questionnaireId}`) || '{}');
        savedState.currentPage = newPage;
        localStorage.setItem(`exam_state_${questionnaireId}`, JSON.stringify(savedState));
      } catch (e) {
        console.error('Could not save page');
      }
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      // Save page to localStorage
      try {
        const savedState = JSON.parse(localStorage.getItem(`exam_state_${questionnaireId}`) || '{}');
        savedState.currentPage = newPage;
        localStorage.setItem(`exam_state_${questionnaireId}`, JSON.stringify(savedState));
      } catch (e) {
        console.error('Could not save page');
      }
    }
  };

  const finishExam = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setEndTime(Date.now());
    
    try {
      const currentAnswers = userAnswersRef.current;
      const currentExamId = examIdRef.current;
      
      // FANG-LEVEL: Single atomic request with ALL answers
      // /submit-and-finish does batch INSERT + score calculation in one round-trip
      const allAnswers = Object.entries(currentAnswers).map(([questionId, answerId]) => ({
        question_id: parseInt(questionId),
        answer_id: parseInt(answerId)
      }));
      
      const { data } = await api.post('/exam/submit-and-finish', {
        exam_id: currentExamId,
        answers: allAnswers,
        offlineTime: offlineTime
      });
      setResult(data);
      setFinished(true);
      
      // Clear localStorage after successful submission
      try {
        localStorage.removeItem(`exam_${questionnaireId}`);
        sessionStorage.removeItem(`exam_${questionnaireId}`);
        localStorage.removeItem(`exam_state_${questionnaireId}`);
        localStorage.removeItem(`exam_order_${questionnaireId}`);
      } catch (e) {
        console.error('Could not clear storage');
      }
    } catch (err) {
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
         const errorMsg = selectedLanguage === 'malayalam' 
            ? "നിങ്ങൾ പരീക്ഷ എഴുതുന്നതിനിടയിൽ കണക്റ്റിവിറ്റി പ്രശ്നം നേരിട്ടിട്ടുണ്ട്. ദയവായി ഇതിന്റെ സ്ക്രീൻഷോട്ട് എടുത്തു വെക്കുകയും, നിങ്ങളുടെ ഉത്തരങ്ങൾ സേവ് ആയോ എന്ന് എക്സാം അഡ്മിനുമായി ബന്ധപ്പെട്ട് ഉറപ്പുവരുത്തുകയും ചെയ്യുക.\n\n(നിങ്ങളുടെ ഇൻ്റർനെറ്റ് പോകുന്നതിൻ്റെ 5 സെക്കൻഡ് മുൻപ് വരെയുള്ള ഉത്തരങ്ങൾ ഓട്ടോ സേവ് ആയിട്ടുണ്ട്)"
            : "You faced a connectivity issue during the quiz. Please take a screenshot and contact the exam admin to check if your answers were submitted.\n\n(Answers up to 5 seconds before the internet disconnected are auto-saved)";
         setError(errorMsg);
         setIsNetworkError(true);
      } else {
         setError(selectedLanguage === 'malayalam' ? "പരീക്ഷ സബ്മിറ്റ് ചെയ്യുന്നതിൽ പിഴവ് സംഭവിച്ചു." : "Failed to finish quiz");
      }
    }
  };

  // Auto-stop if Admin locks the exam
  useEffect(() => {
    if (!started || finished) return;
    const lockTimer = setInterval(async () => {
      try {
        const { data } = await api.get(`/exam/status/${questionnaireId}`);
        if (data.locked) {
          if (!lockPendingRef.current) {
            setAdminLocked(true);
            setLockPending(true);
            
            // Limit remaining time to 60 seconds
            setTimeLeft(t => {
               if (t > 60) return 60;
               return t;
            });
            
            // Adjust startTime so the background throttle-check calculates 60s
            setStartTime(prevStartTime => {
                const now = Date.now();
                return now - ((10 * 60) - 60) * 1000;
            });
          }
        }
      } catch (err) {
        // ignore network errors
      }
    }, 5000);
    return () => clearInterval(lockTimer);
  }, [started, finished, questionnaireId, duration]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getAnswersForQ = (qid) => answers.filter(a => a.question_id === qid);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundPattern}></div>
        <div style={styles.loadingState}>
          <div style={styles.loadingSpinner}>...</div>
          <p style={styles.loadingText}>Loading exam...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const userStr = localStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : {};

    return (
      <div style={styles.container}>
        <div style={styles.backgroundPattern}></div>
        <div style={{
          maxWidth: '600px',
          width: '95%',
          margin: 'clamp(20px, 5vw, 60px) auto',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '24px',
          padding: 'clamp(20px, 5vw, 40px)',
          textAlign: 'center',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{fontSize: 'clamp(40px, 8vw, 60px)', marginBottom: '10px', color: '#e53e3e'}}>⚠️</div>
          <h1 style={{margin: '0 0 15px', fontSize: 'clamp(20px, 6vw, 28px)', fontWeight: '700', color: '#e53e3e'}}>
            {selectedLanguage === 'malayalam' ? 'പിഴവ് (Error)' : 'Error Occurred'}
          </h1>
          
          <h2 style={{fontSize: 'clamp(18px, 5vw, 22px)', color: '#2d3748', margin: '0 0 5px'}}>{userObj.name}</h2>
          <p style={{fontSize: 'clamp(14px, 4vw, 16px)', color: '#718096', marginBottom: 'clamp(15px, 4vw, 25px)', fontWeight: 'bold'}}>Roll No: {userObj.roll_no}</p>

          {isNetworkError && (
            <div style={{ backgroundColor: '#fff5f5', borderLeft: '4px solid #e53e3e', padding: 'clamp(12px, 3vw, 18px)', borderRadius: '8px', marginBottom: 'clamp(20px, 4vw, 30px)', textAlign: 'left' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 'clamp(15px, 4vw, 17px)', color: '#c53030' }}>
                {selectedLanguage === 'malayalam' ? 'ഇന്റർനെറ്റ് ഉറപ്പുവരുത്തി വീണ്ടും സബ്മിറ്റ് ചെയ്യുക!' : 'Please ensure your internet is connected and submit again!'}
              </h3>
              <p style={{ margin: 0, fontSize: 'clamp(13px, 3.5vw, 15px)', color: '#c53030', whiteSpace: 'pre-line', lineHeight: '1.5' }}>{error}</p>
            </div>
          )}

          {!isNetworkError && (
             <p style={{fontSize: 'clamp(14px, 3.5vw, 16px)', color: '#e53e3e', whiteSpace: 'pre-line', marginBottom: 'clamp(15px, 4vw, 25px)'}}>{error}</p>
          )}

          <div style={{display: 'flex', flexDirection: 'column', gap: '12px', width: '100%'}}>
            {isNetworkError && (
              <button onClick={() => { setError(''); setIsNetworkError(false); finishExam(); }} style={{ ...styles.backBtn, background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)', width: '100%' }}>
                {selectedLanguage === 'malayalam' ? 'വീണ്ടും ശ്രമിക്കുക (Retry Submission)' : 'Retry Submission'}
              </button>
            )}
            <button onClick={() => { 
              setError(''); 
              setIsNetworkError(false); 
              const isTest = new URLSearchParams(location.search).get('testChannel') === 'true';
              navigate(isTest ? '/student-react/test-channel' : '/student-react/dashboard'); 
            }} style={{ ...styles.backBtn, background: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)', width: '100%' }}>
              {selectedLanguage === 'malayalam' ? 'ഡാഷ്ബോർഡിലേക്ക് മടങ്ങുക' : 'Back to Dashboard'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (finished && result) {
    const totalSeconds = duration * 60;
    const timeTaken = Math.max(0, totalSeconds - timeLeft);
    const timeTakenMinutes = Math.floor(timeTaken / 60);
    const timeTakenSeconds = timeTaken % 60;
    
    const userStr = localStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : {};
    
    return (
      <div style={styles.container}>
        <div style={styles.backgroundPattern}></div>
        <div style={styles.resultContainer}>
          <div style={{...styles.resultIcon, color: '#38a169'}}>{adminLocked ? '⏱' : '✓'}</div>
          <h1 style={{...styles.resultTitle, color: '#38a169'}}>{adminLocked ? "Sorry, Time's Up!" : "Quiz Submitted Successfully!"}</h1>
          <p style={styles.resultSubtitle}>{adminLocked ? "This quiz was closed by the Admin." : "Your answers have been recorded"}</p>
          
          <div style={{ backgroundColor: '#edf2f7', padding: 'clamp(6px, 2vw, 10px) clamp(12px, 3vw, 20px)', borderRadius: '12px', marginBottom: 'clamp(10px, 3vw, 20px)', display: 'inline-block' }}>
            <h3 style={{ margin: 0, fontSize: 'clamp(14px, 4vw, 18px)', color: '#4a5568' }}>{questionnaire?.title || questionnaire?.name || 'Quiz'}</h3>
          </div>
          
          <h2 style={{fontSize: 'clamp(18px, 5vw, 22px)', color: '#3182ce', margin: '0 0 5px'}}>{userObj.name}</h2>
          <p style={{fontSize: 'clamp(14px, 4vw, 16px)', color: '#3182ce', marginBottom: 'clamp(15px, 4vw, 25px)', fontWeight: 'bold'}}>Roll No: {userObj.roll_no}</p>
          
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{questions.length}</div>
              <div style={styles.statLabel}>Total Questions</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{result.total_questions}</div>
              <div style={styles.statLabel}>Questions Attended</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{timeTakenMinutes}:{timeTakenSeconds.toString().padStart(2, '0')}</div>
              <div style={styles.statLabel}>Time Taken</div>
            </div>
            {offlineTime > 0 && (
              <div style={styles.statItem}>
                <div style={{...styles.statValue, color: '#e53e3e'}}>{Math.floor(offlineTime/60)}m {offlineTime%60}s</div>
                <div style={{...styles.statLabel, color: '#e53e3e', fontWeight: 'bold'}}>Down Time</div>
              </div>
            )}
          </div>
          
          <button onClick={() => {
            const isTest = new URLSearchParams(location.search).get('testChannel') === 'true';
            navigate(isTest ? '/student-react/test-channel' : '/student-react/dashboard');
          }} style={{...styles.backBtn, width: '100%'}}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!started) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundPattern}></div>
        <div style={styles.startContainer}>
          <h1 style={styles.startTitle}>{questionnaire?.title || questionnaire?.name || 'Quiz'}</h1>
          <p style={styles.startSubtitle}>{questionnaire?.purpose || ''}</p>
          
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoIcon}>Q</div>
              <div style={styles.infoLabel}>Questions</div>
              <div style={styles.infoValue}>{questions.length}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoIcon}>T</div>
              <div style={styles.infoLabel}>Duration</div>
              <div style={styles.infoValue}>10 min</div>
            </div>
          </div>
          
          {/* 
          <div style={{ backgroundColor: 'white', padding: 'clamp(15px, 4vw, 25px)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '25px', textAlign: 'left', boxSizing: 'border-box', width: '100%', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            <h3 style={{ textAlign: 'center', color: '#e53e3e', fontSize: 'clamp(18px, 4vw, 20px)', marginBottom: '15px', marginTop: 0 }}>
              <strong>പരീക്ഷാർഥികളുടെ ശ്രദ്ധക്ക്</strong>
            </h3>

            <div style={{ paddingLeft: 'clamp(10px, 3vw, 15px)', color: '#4a5568', lineHeight: '1.6', fontSize: 'clamp(14px, 3.5vw, 15px)', borderLeft: '4px solid #e2e8f0', marginBottom: '20px' }}>
              എക്സാം അറ്റൻഡ് ചെയ്യുന്നതിന് മുമ്പ് ഇവിടെ നൽകിയ നിർദ്ദേശങ്ങൾ വായിച്ചു മനസ്സിലാക്കണം
            </div>

            <h4 style={{ color: '#dd6b20', marginTop: '20px', marginBottom: '15px', fontSize: 'clamp(16px, 3.5vw, 18px)' }}>
              <strong>ശ്രദ്ധിക്കുക</strong>
            </h4>

            <div style={{ paddingLeft: 'clamp(10px, 3vw, 15px)', color: '#4a5568', lineHeight: '1.6', fontSize: 'clamp(14px, 3.5vw, 15px)', borderLeft: '4px solid #dd6b20', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                ഇത് വളരെ പ്രധാനപ്പെട്ട പോയിന്റ് ആണ് - " <strong>മുമ്പത്തെ പേജിലേക്ക് മടങ്ങാൻ ബ്രൗസറിന്റെ back ബട്ടൺ ഉപയോഗിക്കരുത് — അങ്ങനെ ചെയ്താൽ പരീക്ഷ തടസ്സപ്പെടും. വീണ്ടും പരീക്ഷ റീസ്റ്റാർട്ട് ആകും. റീസ്റ്റാർട്ട് ആയാൽ കൂടുതൽ സമയം ലഭിക്കില്ല.</strong> "
              </div>
              <div>മൊത്തം 50 ചോദ്യങ്ങളാണ് പരീക്ഷയിൽ ഉൾപ്പെടുത്തിയിട്ടുള്ളത്.</div>
              <div>എല്ലാ ചോദ്യങ്ങൾക്കും കൂടി ഉത്തരം നൽകാൻ 50 മിനിറ്റ് സമയം ലഭിക്കും.</div>
              <div>ഒരു പേജിൽ 5 ചോദ്യങ്ങൾ ആണ് ഉണ്ടാവുക. അതിനു ഉത്തരം എഴുതി Next button അമർത്തുക.</div>
              <div>
                മുമ്പത്തെ പേജിലേക്ക് മടങ്ങാൻ ബ്രൗസറിന്റെ <strong>'back' ബട്ടൺ യാതൊരു കാരണവശാലും ഉപയോഗിക്കരുത്. അങ്ങനെ ചെയ്താൽ പരീക്ഷ തടസ്സപ്പെടും. വീണ്ടും പരീക്ഷ റീസ്റ്റാർട്ട് ആകും.</strong> പകരം പേജിന്റെ താഴെയുള്ള Previous ബട്ടനാണ് ഉപയോഗിക്കേണ്ടത്.
              </div>
              <div style={{ color: '#e53e3e', fontWeight: 'bold' }}>
                അനുവദിച്ച എക്സാമിനേഷൻ ടൈം കഴിഞ്ഞാൽ അഡ്മിൻ എക്സാം ബ്ലോക്ക് ചെയ്യും. അഡ്മിൻ എക്സാം ബ്ലോക്ക് ചെയ്താൽ പിന്നെ ഒരു മിനിറ്റ് മാത്രമേ ലഭിക്കുകയുള്ളൂ
                <br />
                Time 1:30 pm മുതൽ 2:30pm (മക്ക)
              </div>
            </div>

            <div style={{ marginTop: '20px', color: '#2f855a', lineHeight: '1.6', fontSize: 'clamp(14px, 3.5vw, 15px)', backgroundColor: '#f0fff4', padding: 'clamp(12px, 3vw, 15px)', borderRadius: '8px', borderLeft: '4px solid #38a169' }}>
              അവസാന ചോദ്യം അറ്റൻഡ് ചെയ്ത ശേഷം പച്ച കളറിൽ കാണുന്ന <strong>'Submit'</strong> ബട്ടൺ ക്ലിക്ക് ചെയ്യുമ്പോൾ ഒരു കൺഫേമേഷൻ മെസ്സേജ് വരും. നിങ്ങൾ അവിടെ കൺഫേം ചെയ്താൽ മാത്രമേ submit ആവുകയുള്ളൂ.
            </div>
            
            <div style={{ marginTop: '20px', textAlign: 'center', fontSize: 'clamp(14px, 3.5vw, 16px)', color: '#2d3748', fontWeight: 'bold' }}>
              വായിച്ചു, ബോധ്യപ്പെട്ടു
            </div>
          </div>
          */}

          <button 
            onClick={startExam} 
            style={{
              ...styles.startBtn,
              width: '100%',
              maxWidth: '300px',
              margin: '0 auto',
              display: 'block'
            }}
          >
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  const questionsPerPage = 5;
  const startIndex = currentPage * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = questions.slice(startIndex, endIndex);
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const isLowTime = timeLeft < 60;
  const answeredCount = Object.keys(userAnswers).length;
  const progress = (answeredCount / questions.length) * 100;

  return (
    <div style={styles.container}>
      <div style={styles.backgroundPattern}></div>
      <div style={styles.examContainer}>
        <div style={styles.stickyHeaderWrapper}>
          <div style={styles.header}>
            <div style={styles.progressSection}>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${progress}%` }}></div>
              </div>
              <div style={styles.progressText}>
                Answered {answeredCount} of {questions.length}
              </div>
            </div>
            <div style={{ ...styles.timer, color: isLowTime ? '#c53030' : '#667eea' }}>
              <span style={styles.timerIcon}>Time</span>
              {formatTime(timeLeft)}
            </div>
          </div>
          {lockPending && (
            <div style={{ backgroundColor: '#fff5f5', color: '#c53030', padding: '12px 15px', borderRadius: '8px', borderLeft: '4px solid #c53030', marginTop: '10px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', backdropFilter: 'blur(10px)' }}>
              Admin has stopped the exam, it will auto-submit in 1 minute!
            </div>
          )}
        </div>
        
        {currentQuestions.map((q, qIndex) => (
          <div key={q.id} style={styles.questionCard}>
            <h2 style={styles.question}>
              {startIndex + qIndex + 1}. {selectedLanguage === 'english' ? (q.question_l1 || q.question) : q.question}
            </h2>
            <div style={styles.optionsContainer}>
              {getAnswersForQ(q.id).map((a, index) => (
                <label 
                  key={a.id} 
                  style={{ 
                    ...styles.option, 
                    background: userAnswers[q.id] === a.id ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255, 255, 255, 0.95)',
                    color: userAnswers[q.id] === a.id ? 'white' : '#2d3748',
                    borderColor: userAnswers[q.id] === a.id ? '#667eea' : '#e2e8f0',
                  }}
                >
                  <div style={styles.optionLetter}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <input 
                    type="radio" 
                    name={`question-${q.id}`} 
                    value={a.id} 
                    checked={userAnswers[q.id] === a.id} 
                    onChange={() => handleAnswerSelect(q.id, a.id)}
                    style={styles.radioInput}
                  />
                  <span style={styles.optionText}>
                    {selectedLanguage === 'english' ? (a.answer_l1 || a.answer) : a.answer}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
        
        <div style={styles.pagination}>
          <button 
            onClick={prevPage} 
            disabled={currentPage === 0}
            style={{
              ...styles.navBtn,
              opacity: currentPage === 0 ? 0.5 : 1,
              cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page {currentPage + 1} of {totalPages}
          </span>
          {currentPage < totalPages - 1 ? (
            <button 
              onClick={nextPage}
              style={styles.navBtn}
            >
              Next
            </button>
          ) : (
            <div style={{ width: '100px' }}></div> // Spacer to keep pagination centered
          )}
        </div>

        {currentPage === totalPages - 1 && (
          <button 
            onClick={() => setShowConfirmModal(true)}
            style={{
              width: '100%',
              backgroundColor: '#38a169',
              color: 'white',
              padding: '16px',
              fontSize: '18px',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              marginTop: '30px',
              boxShadow: '0 4px 10px rgba(56, 161, 105, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            Submit Quiz
          </button>
        )}
      </div>

      {/* Styled Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(3px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '16px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <h2 style={{ color: '#2d3748', fontSize: '22px', marginBottom: '25px', marginTop: 0 }}>
              {selectedLanguage === 'malayalam' ? 'ക്വിസ് സബ്മിറ്റ് ചെയ്യുകയാണോ?' : 'Submit Quiz?'}
            </h2>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button 
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#edf2f7',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setShowConfirmModal(false);
                  finishExam();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#38a169',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  flex: 1,
                  boxShadow: '0 4px 6px rgba(56, 161, 105, 0.2)'
                }}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: '0px 20px 20px 20px',
    position: 'relative',
  },
  backgroundPattern: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(102, 126, 234, 0.05) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(118, 75, 162, 0.05) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  examContainer: {
    maxWidth: '900px',
    width: '95%',
    margin: '0 auto',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
  },
  loadingSpinner: {
    fontSize: '64px',
    marginBottom: '24px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    margin: 0,
    fontSize: '18px',
    color: '#718096',
    fontWeight: '500',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '24px',
    padding: '48px',
    backdropFilter: 'blur(10px)',
  },
  errorIcon: {
    fontSize: '64px',
    marginBottom: '24px',
  },
  errorTitle: {
    margin: '0 0 12px',
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
  },
  errorText: {
    margin: '0 0 32px',
    fontSize: '16px',
    color: '#718096',
    textAlign: 'center',
  },
  startContainer: {
    maxWidth: '700px',
    width: '92%',
    margin: '30px auto',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '24px',
    padding: 'clamp(20px, 5vw, 30px)',
    textAlign: 'center',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
  },
  startIcon: {
    fontSize: '64px',
    marginBottom: '24px',
  },
  startTitle: {
    margin: '0 0 12px',
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a202c',
  },
  startSubtitle: {
    margin: '0 0 40px',
    fontSize: '16px',
    color: '#718096',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '32px',
  },
  infoItem: {
    background: '#f7fafc',
    borderRadius: '16px',
    padding: '24px',
  },
  infoIcon: {
    fontSize: '32px',
    marginBottom: '12px',
  },
  infoLabel: {
    fontSize: '13px',
    color: '#718096',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  infoValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    marginTop: '4px',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    background: '#fffaf0',
    border: '1px solid #fbd38d',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '32px',
  },
  warningIcon: {
    fontSize: '20px',
  },
  warningText: {
    fontSize: '14px',
    color: '#744210',
    fontWeight: '500',
  },
  startBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '18px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  },
  examContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    paddingTop: '15px',
  },
  stickyHeaderWrapper: {
    position: 'sticky',
    top: '0',
    zIndex: 100,
    marginBottom: '30px',
    paddingTop: '10px',
    paddingBottom: '10px',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e6eef5 100%)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(226, 232, 240, 0.95)',
    borderRadius: '16px',
    padding: '20px 24px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  },
  progressSection: {
    flex: 1,
    marginRight: '24px',
  },
  progressBar: {
    height: '8px',
    background: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: '500',
  },
  timer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
    fontWeight: '700',
    padding: '12px 20px',
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  },
  timerIcon: {
    fontSize: '20px',
  },
  questionCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '20px',
    padding: '32px',
    marginBottom: '24px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    position: 'relative',
  },
  questionNumber: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '700',
  },
  question: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
    color: '#1a202c',
    lineHeight: '1.6',
    wordBreak: 'break-word',
  },
  translation: {
    margin: '8px 0 0 0',
    fontSize: '18px',
    fontWeight: '400',
    color: '#718096',
    fontStyle: 'italic',
    lineHeight: '1.5',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '32px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 24px',
    borderRadius: '16px',
    border: '2px solid',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  },
  optionLetter: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    flexShrink: 0,
  },
  radioInput: {
    display: 'none',
  },
  optionText: {
    fontSize: '16px',
    fontWeight: '500',
    flex: 1,
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '18px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '18px',
    fontWeight: '700',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '32px',
    padding: '16px',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  },
  navBtn: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    whiteSpace: 'nowrap',
  },
  pageIndicator: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
  },
  resultContainer: {
    maxWidth: '600px',
    width: '95%',
    margin: 'clamp(20px, 5vw, 60px) auto',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '24px',
    padding: 'clamp(20px, 5vw, 48px)',
    textAlign: 'center',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  },
  resultIcon: {
    fontSize: 'clamp(40px, 8vw, 80px)',
    marginBottom: 'clamp(10px, 3vw, 24px)',
  },
  resultTitle: {
    margin: '0 0 8px',
    fontSize: 'clamp(22px, 6vw, 36px)',
    fontWeight: '700',
    color: '#1a202c',
  },
  resultSubtitle: {
    margin: '0 0 clamp(15px, 4vw, 40px)',
    fontSize: 'clamp(14px, 3.5vw, 16px)',
    color: '#718096',
  },
  scoreCard: {
    marginBottom: '40px',
  },
  scoreCircle: {
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
  },
  scorePercentage: {
    fontSize: '56px',
    fontWeight: '700',
    color: 'white',
    lineHeight: 1,
  },
  scoreLabel: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: '8px',
    fontWeight: '500',
  },
  statsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'clamp(8px, 2vw, 16px)',
    marginBottom: 'clamp(20px, 4vw, 40px)',
    justifyContent: 'center',
  },
  statItem: {
    background: '#f7fafc',
    borderRadius: '16px',
    padding: 'clamp(12px, 3vw, 24px)',
    flex: '1',
    minWidth: '80px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: 'clamp(20px, 5vw, 32px)',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: 'clamp(10px, 2.5vw, 13px)',
    color: '#718096',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: 'clamp(12px, 3vw, 16px) clamp(24px, 5vw, 32px)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: 'clamp(14px, 4vw, 16px)',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  },
  btnIcon: {
    fontSize: '18px',
  },
};
